import * as THREE from 'three';

/**
 * @param property - CSS custom property name (e.g. "--color-red-500")
 * @returns trimmed value from :root
 */
export function getCSSColor(property: string): string {
  const rootStyle = getComputedStyle(document.documentElement);
  return rootStyle.getPropertyValue(property).trim();
}

/**
 * @param property - CSS custom property name
 * @returns THREE.Color instance from the resolved CSS value
 */
export function getThreeColor(property: string): THREE.Color {
  const raw = getCSSColor(property);
  return new THREE.Color(normalizeCSSColor(raw));
}

/**
 * Normalises any CSS colour string (including `oklch(...)`, which THREE.Color cannot parse) into a
 * `#rrggbb` string. Modern browsers keep wide-gamut colours in their original space when a canvas
 * `fillStyle` is serialised back, so painting one pixel and reading it back with `getImageData`
 * (which always returns resolved sRGB bytes) is the reliable round-trip — the same technique the
 * sparkle-digit sampler uses for CSS-token colours.
 *
 * @param value - Any CSS colour string
 * @returns An equivalent `#rrggbb` string THREE.Color can parse, or the input unchanged when the browser rejects it or no canvas is available
 */
export function normalizeCSSColor(value: string): string {
  const canvas = document.createElement('canvas');
  canvas.width = 1;
  canvas.height = 1;
  const ctx = canvas.getContext('2d', { willReadFrequently: true });
  if (null === ctx) {
    return value;
  }
  ctx.fillStyle = '#000';
  ctx.fillStyle = value;
  ctx.fillRect(0, 0, 1, 1);
  const [r, g, b] = ctx.getImageData(0, 0, 1, 1).data;
  return `#${((1 << 24) + (r << 16) + (g << 8) + b).toString(16).slice(1)}`;
}
